import { useContext, useState, useRef, useEffect } from "react";
import styles from "./reportSections.module.css";
import { TemplateContext } from "../../../../../../../../contexts/TemplateContext";
import { useVariableMap } from "../../../../variables";
import { Page, emptyPage } from "../../../../../../../../sdkStructs";
import InformationHover from "../../../../../../../Common/Tooltips/InformationHover/InformationHover";
import { Text } from 'lucide-react';
import { defaultPageOrder } from "../../../../../../../../sdkStructs";

export default function ReportSectionsCard() {
  const { template, setTemplate } = useContext(TemplateContext);
  const variableMap = useVariableMap();
  const PLACEHOLDERS = Object.keys(variableMap);

  const hiddenSections = defaultPageOrder
  .filter(page => page.Section === undefined)
  .map(page => page.Name);

  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredPlaceholders, setFilteredPlaceholders] = useState(PLACEHOLDERS);
  const [activeSection, setActiveSection] = useState("");
  const textareaRefs = useRef(new Map<string, HTMLTextAreaElement>());
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isMouseHovering, setIsMouseHovering] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  function selectSection(key: string) {
    return template?.PageOrder.find((page: Page) => page.Name === key);
  }
  function setSections(key: string, value: string) {
    setTemplate((prevTemplate) => {
      if(!prevTemplate) return prevTemplate;
      return {
      ...prevTemplate,
      PageOrder: [
        ...prevTemplate.PageOrder.filter((page: Page) => page.Name !== key),
        {
          ...(selectSection(key) ?? { ...emptyPage }),
          Section: value,
        },
      ],}
    });
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>, key: string) => {
    const value = e.target.value;
    setActiveSection(key);

    const match = value.substring(0, e.target.selectionStart).match(/{{?\s*(\w*)$/);
    if (match) {
      const query = match[1].toLowerCase();
      setShowDropdown(true);
      setFilteredPlaceholders(PLACEHOLDERS.filter((placeholder) => placeholder.toLowerCase().includes(query)));
    } else {
      setShowDropdown(false);
    }
    setSections(key, value);
  };

  const handlePlaceholderSelect = (placeholder: string) => {
    const textareaRef = textareaRefs.current.get(activeSection);
    if (!textareaRef) return;

    const selectionStart = textareaRef.selectionStart || 0;
    const textBefore = selectSection(activeSection)
      ?.Section?.substring(0, selectionStart)
      .replace(/{{?\s*\w*$/, "{{ ");
    const textAfter = selectSection(activeSection)?.Section?.substring(selectionStart);

    const value = textBefore + placeholder + " }}" + textAfter;

    setSections(activeSection, value);

    setShowDropdown(false);

    // Set focus and cursor position after state update
    setTimeout(() => {
      textareaRef.focus();
      const newCursorPosition = (textBefore + placeholder + " }}").length;
      textareaRef.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showDropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % filteredPlaceholders.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev - 1 + filteredPlaceholders.length) % filteredPlaceholders.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        handlePlaceholderSelect(filteredPlaceholders[highlightedIndex]);
      } else if (e.key === "Escape") {
        setShowDropdown(false);
      }
    }
  };

  useEffect(() => {
    if (dropdownRef.current) {
      const highlightedItem = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedItem) {
        highlightedItem.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text className={styles.icon} />
        <h1>Report Sections</h1>
        <div className={styles.tipContainer}>
          <InformationHover lightbulb description="Use `{{` to insert variables and `#` for headings." />
        </div>
      </div>

      <div className={styles.content}>
        {template?.PageOrder
          .filter(({ Name }) => !hiddenSections.includes(Name))
          .sort((a, b) => a.Position > b.Position ? 1 : -1)
          .map(({ Name, Section }) => {
            if (Section === undefined) return;
            else
              return (
                <div key={Name} className={styles.section}>
                  <label htmlFor={`${Name}-input`}>{Name.charAt(0).toUpperCase() + Name.slice(1)}</label>
                  <textarea
                    ref={(el) => {
                      if (el) textareaRefs.current.set(Name, el);
                    }}
                    id={`${Name}-input`}
                    value={Section}
                    onChange={(e) => handleInputChange(e, Name)}
                    onKeyDown={handleKeyDown}
                    placeholder={`Enter text for page ${Name} here...`}
                    className={styles.textarea}
                  />
                  {showDropdown && activeSection === Name && (
                    <div className={styles.dropdown} ref={dropdownRef}>
                      {filteredPlaceholders.map((placeholder, index) => (
                        <div
                          key={Name}
                          className={`${styles.dropdownItem} ${index === highlightedIndex && !isMouseHovering ? styles.highlighted : ""}`}
                          onClick={() => handlePlaceholderSelect(placeholder)}
                          onMouseEnter={() => setIsMouseHovering(true)}
                          onMouseLeave={() => setIsMouseHovering(false)}
                        >
                          {`{{ ${placeholder} }}`}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
          })}
      </div>
    </div>
  );
}
